/**
 * PickingSlipScanFlow — iter 290zp.6.
 *
 * Scan-to-fill UX for printed picking slips. Two entry points (both
 * render the same review screen):
 *
 *   1. **Manual** — "Scan slip" button on PickingListDetail. Opens the
 *      file picker / camera capture.
 *   2. **QR auto-prompt** — URL carries `?print_id=...` (set by the QR
 *      printed on the slip's navy band). On mount we surface a banner
 *      "Print #N detected — scan slip to auto-fill?" with a one-tap
 *      camera button.
 *
 * Once the photo is uploaded the backend:
 *   - Stores it in R2 + creates a `customer_profile_documents` row
 *     (category="picking_scan") so it's findable under the customer's
 *     Docs tab.  NOT shown in the customer portal.
 *   - Calls GPT-4o vision and returns parsed JSON per row.
 *
 * The review step shows a per-row diff against the live values, with
 * editable cells (user holds the physical slip next to the screen and
 * overrides any misread).  Apply modes:
 *   • Override all (default — keeps slips & app in lock-step)
 *   • Add all     (sums scan + current)
 *   • Per-row     (pick mode per cell)
 *
 * Blank cells in the scan are skipped — never zero-out existing data.
 * Notes are APPENDED to the list's notes blob, never overwritten.
 *
 * Signed-off lists park the scan as "pending_review" — only admins can
 * force-apply.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Camera, Loader2, X, Check, AlertTriangle, ScanLine, Info } from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import DictTextarea from "../DictTextarea";

const MODE_OPTIONS = [
  { v: "override_all", l: "Override all", hint: "Scan wins. Keeps slips & app in sync (recommended)." },
  { v: "add_all",      l: "Add all",      hint: "Sum scan + current values." },
  { v: "per_row",      l: "Per row",      hint: "Pick mode per cell. Slowest." },
];

const FIELDS = [
  { k: "picked",     l: "Picked",     color: "emerald" },
  { k: "dispatched", l: "Dispatched", color: "sky" },
  { k: "returned",   l: "Returned",   color: "violet" },
  { k: "site_used",  l: "Site used",  color: "teal" },
];

const fmtN = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  return Number.isInteger(x) ? String(x) : x.toFixed(2).replace(/\.?0+$/, "");
};

export default function PickingSlipScanFlow({ listId, isSignedOff, onApplied }) {
  const [params, setParams] = useSearchParams();
  const qrPrintId = params.get("print_id") || null;
  const [showCapture, setShowCapture] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scan, setScan] = useState(null);          // server response after vision
  const [snapshot, setSnapshot] = useState(null);  // items_snapshot
  const [decisions, setDecisions] = useState({});  // { rowId: { field: { value, mode? } } }
  const [mode, setMode] = useState("override_all");
  const [applying, setApplying] = useState(false);
  const [notesAdd, setNotesAdd] = useState("");
  const fileRef = useRef(null);

  // Auto-prompt when arriving from a QR scan.
  const [qrPrompt, setQrPrompt] = useState(!!qrPrintId);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (qrPrintId) fd.append("print_id", qrPrintId);
      const resp = await api.post(`/picking/lists/${listId}/scan`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      const data = resp.data;
      if (data.status === "failed") {
        toast.error(`Vision failed: ${data.error || "unknown"}`);
        setShowCapture(false);
        return;
      }
      setScan(data);
      setSnapshot(data.items_snapshot || []);
      // Seed decisions: for every scanned cell with a value, pre-fill
      // the user's "final value" with the OCR-read number. They can
      // edit any cell before applying.
      const init = {};
      for (const r of (data.parsed?.rows || [])) {
        init[r.row_id] = {};
        for (const f of ["picked", "dispatched", "returned", "site_used"]) {
          const v = r[f]?.value;
          if (v !== null && v !== undefined) init[r.row_id][f] = { value: String(v) };
        }
      }
      setDecisions(init);
      setNotesAdd(data.parsed?.notes_text || "");
      setShowCapture(false);
      setQrPrompt(false);
      // Strip print_id from URL so a refresh doesn't re-prompt.
      if (qrPrintId) {
        params.delete("print_id");
        setParams(params, { replace: true });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not upload scan.");
    } finally {
      setUploading(false);
    }
  };

  const apply = async () => {
    if (!scan?.scan_id) return;
    setApplying(true);
    try {
      const rows = Object.entries(decisions).map(([rowId, cells]) => {
        const rowDec = { row_id: rowId };
        for (const f of ["picked", "dispatched", "returned", "site_used"]) {
          const c = cells[f];
          if (c && c.value !== "" && c.value !== null && c.value !== undefined) {
            const numV = Number(c.value);
            if (Number.isFinite(numV) && numV >= 0) {
              rowDec[f] = { value: numV, mode: c.mode || null };
            }
          }
        }
        return rowDec;
      }).filter(r => Object.keys(r).length > 1); // must have at least one field
      const resp = await api.post(`/picking/scans/${scan.scan_id}/apply`, {
        mode,
        rows,
        notes_text: notesAdd.trim() || null,
      });
      if (resp.data.status === "pending_review") {
        toast.warning("List is signed off — scan parked for admin review.");
      } else {
        const c = resp.data.counts || {};
        toast.success(
          `Applied · ${c.picked || 0} picked · ${c.dispatched || 0} disp · ${c.returned || 0} ret · ${c.site_used || 0} site-used`,
        );
      }
      setScan(null);
      setSnapshot(null);
      setDecisions({});
      setNotesAdd("");
      onApplied?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(e?.response?.data?.detail || "Apply failed.");
    } finally {
      setApplying(false);
    }
  };

  // ────────── Banners + main button ──────────
  return (
    <>
      {qrPrompt && (
        <div
          className="mb-3 rounded border border-cyan-400/30 bg-cyan-500/[0.08] p-3 flex items-center gap-3"
          data-testid="picking-scan-qr-prompt"
        >
          <ScanLine className="w-4 h-4 text-cyan-300 shrink-0" />
          <div className="flex-1 text-[12px] text-cyan-100">
            <strong className="font-semibold">Hard-copy slip detected.</strong>
            {" "}Tap below to scan the slip you filled in and we&apos;ll auto-fill the values.
          </div>
          <button
            type="button"
            onClick={() => { setShowCapture(true); }}
            className="px-3 py-1.5 rounded bg-cyan-500/30 hover:bg-cyan-500/40 border border-cyan-400/50 text-[12px] font-medium text-cyan-50 inline-flex items-center gap-1.5"
            data-testid="picking-scan-qr-start"
          >
            <Camera className="w-3.5 h-3.5" /> Scan slip
          </button>
          <button
            type="button"
            onClick={() => {
              setQrPrompt(false);
              params.delete("print_id");
              setParams(params, { replace: true });
            }}
            className="text-cyan-300 hover:text-cyan-100 p-1"
            data-testid="picking-scan-qr-dismiss"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Manual entry point — always visible button */}
      <button
        type="button"
        onClick={() => setShowCapture(true)}
        disabled={uploading}
        data-testid="picking-detail-scan-slip"
        title="Scan a hand-filled picking slip and auto-fill the values"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-cyan-400/30 bg-cyan-500/[0.08] text-[11px] text-cyan-100 hover:bg-cyan-500/[0.18] disabled:opacity-50 transition"
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
        <span>{uploading ? "Scanning…" : "Scan slip"}</span>
      </button>

      {/* Capture sheet */}
      {showCapture && !scan && (
        <CaptureSheet
          uploading={uploading}
          onClose={() => setShowCapture(false)}
          onSelect={handleUpload}
          fileRef={fileRef}
        />
      )}

      {/* Review screen */}
      {scan && (
        <ReviewSheet
          scan={scan}
          snapshot={snapshot}
          decisions={decisions}
          setDecisions={setDecisions}
          mode={mode}
          setMode={setMode}
          notesAdd={notesAdd}
          setNotesAdd={setNotesAdd}
          isSignedOff={isSignedOff}
          applying={applying}
          onApply={apply}
          onClose={() => { setScan(null); setSnapshot(null); setDecisions({}); setNotesAdd(""); }}
        />
      )}
    </>
  );
}

// ─────────────────────── Capture sheet ───────────────────────

function CaptureSheet({ uploading, onClose, onSelect, fileRef }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
      data-testid="picking-scan-capture-sheet"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-[#0d1219] border border-white/10 rounded-t-xl sm:rounded-xl p-4 sm:p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <ScanLine className="w-5 h-5 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Scan picking slip</h3>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-[#647087] hover:text-white p-1"
            data-testid="picking-scan-capture-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[12px] text-[#a0aabf] mb-4">
          Lay the slip on a flat surface in good light. Capture from directly above so the rows align cleanly.
          The OCR reads <b>handwritten arithmetic</b> too — write <code className="text-cyan-300">5+5</code> or strike-through and rewrite, both will be detected.
        </p>
        <div className="space-y-2">
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-400/40 text-cyan-100 font-medium disabled:opacity-50"
            data-testid="picking-scan-capture-button"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            {uploading ? "Uploading & reading…" : "Take photo / choose file"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onSelect(f);
              e.target.value = "";
            }}
          />
        </div>
        {uploading && (
          <p className="mt-3 text-[11px] text-[#647087] text-center">
            Vision call typically takes 4-8 seconds. Stay on this page.
          </p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────── Review sheet ─────────────────────────

function ReviewSheet({
  scan, snapshot, decisions, setDecisions,
  mode, setMode, notesAdd, setNotesAdd,
  isSignedOff, applying, onApply, onClose,
}) {
  // Build a row-id → vision-row map for fast lookup.
  const visionByRow = useMemo(() => {
    const m = {};
    for (const r of (scan?.parsed?.rows || [])) m[r.row_id] = r;
    return m;
  }, [scan]);

  // Count rows that DIFFER from the current app value (mismatch badge).
  const diffCount = useMemo(() => {
    let n = 0;
    for (const it of (snapshot || [])) {
      const v = visionByRow[it.id];
      if (!v) continue;
      for (const f of ["picked", "dispatched", "returned", "site_used"]) {
        const scanned = v[f]?.value;
        if (scanned === null || scanned === undefined) continue;
        const live = {
          picked: it.qty_picked, dispatched: it.qty_dispatched,
          returned: it.qty_returned, site_used: it.qty_site_used,
        }[f] || 0;
        if (Math.abs(Number(scanned) - Number(live || 0)) > 0.0001) {
          n += 1;
          break;
        }
      }
    }
    return n;
  }, [snapshot, visionByRow]);

  const visionRows = scan?.parsed?.rows || [];

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/80 flex items-stretch justify-center p-0 sm:p-4 overflow-y-auto"
      data-testid="picking-scan-review-sheet"
    >
      <div className="w-full max-w-6xl bg-[#0d1219] border border-white/10 sm:rounded-xl flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-5 py-3 border-b border-white/10 flex items-center gap-3 sticky top-0 bg-[#0d1219] z-10">
          <ScanLine className="w-5 h-5 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Review scan</h3>
          <span className="text-[11px] text-[#647087] font-mono">{scan.scan_id?.slice(0, 8)}</span>
          {isSignedOff && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/20 border border-amber-400/40 text-amber-200 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Signed off — pending review
            </span>
          )}
          {diffCount > 0 && (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-cyan-500/20 border border-cyan-400/40 text-cyan-100">
              {diffCount} row{diffCount > 1 ? "s" : ""} differ
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            className="ml-auto text-[#647087] hover:text-white p-1"
            data-testid="picking-scan-review-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode picker */}
        <div className="px-4 sm:px-5 py-3 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-2">Apply mode</div>
          <div className="flex flex-wrap gap-2">
            {MODE_OPTIONS.map((m) => (
              <button
                key={m.v}
                type="button"
                onClick={() => setMode(m.v)}
                data-testid={`picking-scan-mode-${m.v}`}
                className={`text-[12px] px-3 py-1.5 rounded border transition ${
                  mode === m.v
                    ? "bg-cyan-500/30 border-cyan-400/60 text-cyan-50 font-semibold"
                    : "bg-white/[0.03] border-white/10 text-[#a0aabf] hover:bg-white/[0.06]"
                }`}
              >
                <div>{m.l}</div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#647087] mt-2 flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            {MODE_OPTIONS.find(m => m.v === mode)?.hint}
            {mode !== "override_all" && (
              <span className="text-amber-300/80 ml-1">
                Heads up — mixing modes means physical slips may no longer match the app values.
              </span>
            )}
          </p>
        </div>

        {/* Rows table */}
        <div className="flex-1 overflow-auto px-2 sm:px-3 py-2">
          {visionRows.length === 0 ? (
            <div className="text-center text-[#647087] py-12">
              The vision couldn&apos;t read any rows from this photo. Try a clearer image.
            </div>
          ) : (
            <table className="w-full text-[11px] table-fixed">
              <thead className="sticky top-0 bg-[#0d1219] z-[1]">
                <tr className="text-left text-[9px] uppercase tracking-wider text-[#647087]">
                  <th className="py-2 px-2 w-[24%]">Item</th>
                  {FIELDS.map((f) => (
                    <th key={f.k} className="py-2 px-1 w-[15%]">{f.l}</th>
                  ))}
                  <th className="py-2 px-1 w-[16%] text-right">Per-row mode</th>
                </tr>
              </thead>
              <tbody>
                {visionRows.map((r) => {
                  const snap = (snapshot || []).find(s => s.id === r.row_id);
                  if (!snap) return null;
                  return (
                    <ReviewRow
                      key={r.row_id}
                      vision={r}
                      snap={snap}
                      decision={decisions[r.row_id] || {}}
                      setCell={(field, patch) => {
                        setDecisions((prev) => ({
                          ...prev,
                          [r.row_id]: {
                            ...(prev[r.row_id] || {}),
                            [field]: { ...(prev[r.row_id]?.[field] || {}), ...patch },
                          },
                        }));
                      }}
                      modeIsPerRow={mode === "per_row"}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Notes append */}
        <div className="px-4 sm:px-5 py-3 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1.5">
            Notes (append to the picking list)
          </div>
          <DictTextarea
            value={notesAdd}
            onChange={(e) => setNotesAdd(e.target.value)}
            rows={2}
            placeholder="Anything written in the Notes section of the slip…"
            className="hv-input w-full text-[12px] py-2"
            data-testid="picking-scan-notes-add"
          />
          <p className="text-[10px] text-[#647087] mt-1">
            Existing notes are kept — this is appended with a date / picker prefix.
          </p>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-3 border-t border-white/10 flex items-center gap-2 sticky bottom-0 bg-[#0d1219]">
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            className="text-[12px] px-3 py-1.5 rounded border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-[#a0aabf] disabled:opacity-50"
            data-testid="picking-scan-review-cancel"
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onApply}
            disabled={applying || visionRows.length === 0}
            className="text-[13px] px-4 py-2 rounded bg-emerald-500/30 hover:bg-emerald-500/40 border border-emerald-400/50 text-emerald-50 font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="picking-scan-review-apply"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {applying ? "Applying…" : (isSignedOff ? "Park for admin review" : "Apply scan")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ vision, snap, decision, setCell, modeIsPerRow }) {
  return (
    <tr className="border-t border-white/[0.04]">
      <td className="py-1.5 px-2 align-top">
        <div className="font-mono text-cyan-300 text-[10px]">{snap.sku || "—"}</div>
        <div className="text-white truncate text-[11px]" title={snap.name}>{snap.name}</div>
        <div className="text-[#647087] text-[9px] mt-0.5">Req {fmtN(snap.qty_required)}</div>
      </td>
      {FIELDS.map((f) => {
        const v = vision[f.k];
        const live = {
          picked: snap.qty_picked, dispatched: snap.qty_dispatched,
          returned: snap.qty_returned, site_used: snap.qty_site_used,
        }[f.k];
        const dec = decision[f.k] || {};
        const userVal = dec.value;
        const numUser = Number(userVal);
        const scanned = v?.value;
        const hasScanned = scanned !== null && scanned !== undefined;
        const liveNum = Number(live || 0);
        const differs = hasScanned && Math.abs(Number(scanned) - liveNum) > 0.0001;
        const conf = v?.confidence;
        const derivation = v?.derivation;
        return (
          <td key={f.k} className="py-1.5 px-1 align-top">
            <div className="text-[9px] text-[#647087]">
              Live: <span className="text-white">{fmtN(live)}</span>
              {hasScanned && (
                <>
                  {" → "}
                  <span className={differs ? "text-cyan-300" : "text-[#a0aabf]"}>{fmtN(scanned)}</span>
                  {typeof conf === "number" && (
                    <span className={`ml-1 ${conf >= 0.85 ? "text-emerald-400/70" : conf >= 0.6 ? "text-amber-400/70" : "text-rose-400/70"}`}>
                      ({Math.round(conf * 100)}%)
                    </span>
                  )}
                </>
              )}
            </div>
            <input
              type="number"
              min="0"
              step="any"
              value={userVal ?? ""}
              onChange={(e) => setCell(f.k, { value: e.target.value })}
              placeholder={hasScanned ? "" : "—"}
              className={`hv-input w-full text-[12px] font-mono py-0.5 px-1 mt-0.5 ${
                differs && (userVal === undefined || userVal === "" || Math.abs(numUser - liveNum) > 0.0001)
                  ? "border-cyan-400/40 bg-cyan-500/[0.06]"
                  : ""
              }`}
              data-testid={`picking-scan-cell-${snap.id}-${f.k}`}
            />
            {derivation && (
              <div className="text-[9px] text-amber-300/80 mt-0.5 truncate" title={derivation}>
                ✎ {derivation}
              </div>
            )}
            {modeIsPerRow && (
              <select
                value={dec.mode || "override"}
                onChange={(e) => setCell(f.k, { mode: e.target.value })}
                className="hv-input w-full text-[10px] py-0.5 px-1 mt-0.5"
                data-testid={`picking-scan-cell-mode-${snap.id}-${f.k}`}
              >
                <option value="override">Override</option>
                <option value="add">Add</option>
                <option value="ignore">Ignore</option>
              </select>
            )}
          </td>
        );
      })}
      <td className="py-1.5 px-1 text-right text-[9px] text-[#647087]">
        {modeIsPerRow ? "per-cell ↑" : "—"}
      </td>
    </tr>
  );
}
